import type { KeyboardEvent, MutableRefObject } from 'react';
import React, { useRef, useState } from 'react';
import type { IntlShape } from 'react-intl-next';
import { FormattedMessage, injectIntl } from 'react-intl-next';
import { SpotlightManager, SpotlightTarget, EngagementSpotlight } from '@atlassiansox/engagekit-ts';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import type { SearchCSS } from '@atlaskit/atlassian-navigation';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { ConfluenceFeatures, JiraFeatures } from '@atlassian/product-search-dialog';

import { AddActivationIdWrapper } from '@confluence/analytics/entry-points/AddActivationIdWrapper.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { TimeAgo } from '@confluence/time-ago';
import {
	SessionData,
	type SessionDataChildrenFn,
} from '@confluence/session-data/entry-points/SessionData.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { isMac } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	NAVIGATION_SHORTCUT,
	NAVIGATION_SLASH_SHORTCUT,
	UNIVERSAL_NAVIGATION_SHORTCUT_MAC,
	UNIVERSAL_NAVIGATION_SHORTCUT_WIN,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { PageSubscriber } from '@confluence/page-context';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

import type { ClientProviderConfig } from './ClientProvider.tsx';
import { ClientProvider } from './ClientProvider.tsx';
import { getResultLinkComponent } from './ResultLinkComponent.tsx';
import type { UserContextDataPayload } from './UserContext.tsx';
import { UserContext } from './UserContext.tsx';
import { CurrentSpaceContext } from './CurrentSpaceContext.tsx';
import { QuickFindNav3 } from './QuickFindNav3.tsx';
import {
	generateInteractiveSkeletonOnEnter,
	InteractiveSearchInputSkeleton,
} from './InteractiveSearchInputSkeleton.tsx';
import { ExtensibleCrossProductSearchDialog } from './ExtensibleCrossProductSearchDialog.tsx';
import { i18n } from './messages.ts';
import { useQuickFindEnabled } from './experiment/QuickFindExperiment.tsx';

export const SEARCH_SPOTLIGHT_KEY = 'search-spotlight';

export interface NavigationSearchDialogProps {
	theme?: SearchCSS;
	clearStickySearchRef?: MutableRefObject<() => void>;
	shouldFillContainer?: boolean;
}

export const formatDate = (date: string) => {
	const timeAgo = <TimeAgo date={date} dateFormatOptions={{ month: 'short' }} />;

	return <FormattedMessage {...i18n.lastUpdatedDate} values={{ datetime: timeAgo }} />;
};

const analyticsContainerStyles = xcss({
	width: '100%',
});

export type LinkComponent = React.ComponentType<{
	href?: string;
	target?: '_blank' | '_self' | '_parent' | '_top';
	className?: string;
	isKeyboardHighlighted?: boolean;
	isCollapsed?: boolean;
}>;

interface InnerProps {
	isExpanded: boolean;
	onOpen: (e?: KeyboardEvent | React.MouseEvent<Element>) => void;
	onClose: () => void;
	formatDate: (lastModified: string) => JSX.Element;
	ref: React.RefObject<HTMLInputElement>;
	theme?: SearchCSS;
	confluenceFeatures?: ConfluenceFeatures;
	jiraFeatures?: JiraFeatures;
	user: UserContextDataPayload;
	onNavigate: (url: string) => void;
	linkComponent: LinkComponent;
	shouldFillContainer?: boolean;
}

const NavigationSearchDialogWithRouter: React.FC<
	{ intl: IntlShape } & NavigationSearchDialogProps
> = ({ theme, shouldFillContainer, intl, clearStickySearchRef }) => {
	const [isOpen, setIsOpen] = useState(false);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const { push } = useRouteActions();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const openDialog = (
		e:
			| Mousetrap.ExtendedKeyboardEvent
			| KeyboardEvent<Element>
			| React.MouseEvent<Element>
			| null
			| undefined,
	) => {
		if (e) {
			e.preventDefault(); //prevent shortcut key from being added to search input
			searchInputRef.current?.focus();
		}
		setIsOpen(true);
	};

	const closeDialog = () => {
		setIsOpen(false);
	};

	const onResultLinkClick = () => {
		closeDialog();
	};

	const onNavigate = (url: string) => {
		closeDialog();
		push(url);
	};

	const onInteractiveSkeletonEnter = generateInteractiveSkeletonOnEnter(push);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'search',
				source: 'globalNavigation',
				attributes: {
					navigationLayer: 'global',
					navigationContainer: 'top',
					navVersion: '4',
				},
			},
		}).fire();
	};

	const getRootSearchDialog = (
		{ ref, ...rest }: InnerProps,
		clientProviderConfig: ClientProviderConfig,
	) => {
		const { cloudId, aggregatorUrl } = clientProviderConfig;
		const interactiveSkeleton = {
			onEnter: onInteractiveSkeletonEnter,
			placeholder: intl.formatMessage(i18n.placeholder),
		};

		return (
			<SpotlightManager>
				<SpotlightTarget name={SEARCH_SPOTLIGHT_KEY}>
					{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
					<Box
						xcss={analyticsContainerStyles}
						onClick={fireClickAnalytics}
						data-vc="app-navigation-search-dialog"
						data-ssr-placeholder-replace="search-input"
					>
						<AddActivationIdWrapper
							channel="fabric-elements"
							targetActionSubjects={['postQuerySearchResults', 'cachedResults']}
						>
							<ExtensibleCrossProductSearchDialog
								jiraFeatures={{ hasSoftwareAccess: true }}
								forwardRef={ref}
								setIsExpanded={(value: boolean) => (value ? openDialog(null) : closeDialog())}
								cloudId={cloudId}
								aggregatorUrl={aggregatorUrl}
								onNavigateSecondary={(href: string) => {
									// eslint-disable-next-line no-restricted-syntax
									window.location.assign(href);
								}}
								features={{
									jiraFeatures: rest.jiraFeatures,
									confluenceFeatures: rest.confluenceFeatures,
									interactiveSkeleton,
								}}
								clearStickySearchRef={clearStickySearchRef}
								{...rest}
							/>
						</AddActivationIdWrapper>
					</Box>
				</SpotlightTarget>
				<EngagementSpotlight isMigrated engagementId={SEARCH_SPOTLIGHT_KEY} />
			</SpotlightManager>
		);
	};

	const getSearchDialog = (
		searchDialogProps: InnerProps,
		clientProviderConfig: ClientProviderConfig,
	) => {
		const autoAddCurrentSpaceIsEnabled = false;

		return (
			<PageSubscriber>
				{({ spaceKey }) => {
					return !!spaceKey && autoAddCurrentSpaceIsEnabled ? (
						<CurrentSpaceContext spaceKey={spaceKey}>
							{({ currentSpace, loading }) => {
								if (loading) {
									return (
										<InteractiveSearchInputSkeleton
											theme={searchDialogProps.theme}
											shouldFillContainer={searchDialogProps.shouldFillContainer}
										/>
									);
								} else {
									const currentSpaceValid =
										currentSpace && Object.values(currentSpace).every((v) => v !== undefined);
									return getRootSearchDialog(
										{
											...searchDialogProps,
											confluenceFeatures: {
												currentSpace: currentSpaceValid
													? (currentSpace as ConfluenceFeatures['currentSpace'])
													: undefined,
											},
										},
										clientProviderConfig,
									);
								}
							}}
						</CurrentSpaceContext>
					) : (
						getRootSearchDialog(
							{
								...searchDialogProps,
							},
							clientProviderConfig,
						)
					);
				}}
			</PageSubscriber>
		);
	};

	const universalNavigationShortcut = isMac()
		? UNIVERSAL_NAVIGATION_SHORTCUT_MAC
		: UNIVERSAL_NAVIGATION_SHORTCUT_WIN;

	return (
		<>
			<SessionData>
				{
					(({ cloudId, userId }) => {
						return (
							<ClientProvider cloudId={cloudId} userId={userId!} useUrsForPeopleSearch={false}>
								{({ clientProviderConfig }) => (
									<ErrorBoundary attribution={Attribution.DISCO} modal>
										<UserContext>
											{({ data, isLoading }) => {
												return isLoading ? (
													<InteractiveSearchInputSkeleton
														theme={theme}
														shouldFillContainer={shouldFillContainer}
													/>
												) : (
													getSearchDialog(
														{
															isExpanded: isOpen,
															onOpen: openDialog,
															onClose: closeDialog,
															linkComponent: getResultLinkComponent(onResultLinkClick),
															formatDate,
															ref: searchInputRef,
															theme,
															user: {
																name: data && data.name,
																email: data && data.email,
																id: data && data.id,
															},
															onNavigate,
															shouldFillContainer,
														},
														clientProviderConfig,
													)
												);
											}}
										</UserContext>
									</ErrorBoundary>
								)}
							</ClientProvider>
						);
					}) as SessionDataChildrenFn
				}
			</SessionData>
			<GeneralShortcutListener
				deprecatedAccelerator={!(isLiveEditMode || isOnEditRoute) ? NAVIGATION_SHORTCUT : undefined}
				accelerator={
					isLiveEditMode || isOnEditRoute
						? universalNavigationShortcut
						: [universalNavigationShortcut, NAVIGATION_SLASH_SHORTCUT]
				}
				listener={openDialog}
				isAppNavigationShortcut
			/>
		</>
	);
};

const NavigationSearchDialogInternal = injectIntl(NavigationSearchDialogWithRouter);

export const NavigationSearchDialog = (props: NavigationSearchDialogProps) => {
	const [isQuickFindEnabled] = useQuickFindEnabled();
	return isQuickFindEnabled ? <QuickFindNav3 /> : <NavigationSearchDialogInternal {...props} />;
};
